import { hash } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { keepLatestTask } from 'ember-concurrency';
import { type PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type Invite from 'garaje/models/invite';
import type InviteModel from 'garaje/models/invite';
import type { InviteWithVisitApproval } from 'garaje/models/invite';
import type { ApprovalsFilters } from 'garaje/pods/visitors/approvals/index/template';
import { TrackedArray } from 'tracked-built-ins';

interface VisitorsApprovalsTableDataSignature {
  Args: {
    filters: ApprovalsFilters;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
  };
  Blocks: {
    default: [
      {
        invites: TrackedArray<InviteWithVisitApproval>;
        loading: boolean;
        reload: () => void;
        removeInvites: (invitesToRemove: InviteModel[]) => void;
      },
      { hasMorePages: boolean; loadMore: () => void },
    ];
  };
}

const PAGE_SIZE = 20;

// Map exposed sort param names to the ones the API uses. This allows us to check that a user-specified
// param is valid before passing it to the API (which could return with an HTTP 400 error and break the page).
const SORT_FIELDS: { [key: string]: string } = {
  dueAt: 'expected_arrival_time',
  name: 'full_name',
  type: 'flows.name',
};
const DEFAULT_SORT = 'expected_arrival_time';

export default class VisitorsApprovalsTableData extends Component<VisitorsApprovalsTableDataSignature> {
  @service declare store: Store;

  @tracked invites: TrackedArray<InviteWithVisitApproval> = new TrackedArray();

  #currentOffset = 0;
  #totalInvites = 0;

  constructor(owner: unknown, args: VisitorsApprovalsTableDataSignature['Args']) {
    super(owner, args);

    void this.loadInvites.perform(false);
  }

  get sort(): string {
    const sortField = SORT_FIELDS[this.args.sortBy] || DEFAULT_SORT;
    return this.args.sortDirection === 'desc' ? `-${sortField}` : sortField;
  }

  loadInvites = keepLatestTask(async (reset: boolean) => {
    if (reset) {
      this.invites = new TrackedArray();
      this.#currentOffset = 0;
      this.#totalInvites = 0;
    }
    const filter = {
      'approval-status': 'review',
      ...this.args.filters,
    };

    const include = ['visit-approval', 'visit-approval.screener-results'];
    const invites = <PaginatedRecordArray<InviteWithVisitApproval>>await this.store.query('invite', {
      filter,
      page: {
        limit: PAGE_SIZE,
        offset: this.#currentOffset,
      },
      sort: this.sort,
      include: include.join(),
    });
    const invitesArray = invites.slice();
    this.#currentOffset += invitesArray.length;
    this.#totalInvites = invites.meta.total;

    this.invites.push(...invitesArray);
  });

  get hasMorePages(): boolean {
    return this.invites.length < this.#totalInvites;
  }

  @action
  reloadData(): void {
    void this.loadInvites.perform(true);
  }

  @action
  removeInvites(invitesToRemove: Invite[]): void {
    this.invites = new TrackedArray(this.invites.filter((invite) => !invitesToRemove.includes(invite)));
    this.#totalInvites -= invitesToRemove.length;
  }

  @action
  loadMore(): void {
    if (this.hasMorePages) {
      void this.loadInvites.perform(false);
    }
  }

  <template>
    {{yield
      (hash
        invites=this.invites loading=this.loadInvites.isRunning reload=this.reloadData removeInvites=this.removeInvites
      )
      (hash hasMorePages=this.hasMorePages loadMore=this.loadMore)
    }}
  </template>
}
